import { memo, useCallback } from 'react';

import { usePipelineRunHistoryContext } from 'src/components/pipelines/contexts/pipeline-run-history-context';
import {
  Account,
  Checks,
  ChildPipelineStep,
  Deployment,
  Pipeline,
  Repository,
  Step,
} from 'src/components/pipelines/models';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { StatsigFeatureKeys } from 'src/features';

import {
  MetricUsageStatusType,
  METRICS_USAGE_ERROR_LIMIT,
  STEP_METRICS_ENABLED,
} from '../../constants';
import { getFeatureFlagFromStep } from '../../utils/getFeatureFlagFromStep';
import { PipelineWarningWrapper } from '../styled';

import HaltedMessage from './HaltedMessage';
import { MetricsUsageMessage } from './MetricsUsageMessage';
import { PreviousRunMessage } from './PreviousRunMessage';
import WarningMessage from './WarningMessage';

export interface Props {
  account: Account;
  repository: Repository;
  pipeline: Pipeline;
  step: Step | ChildPipelineStep;
  steps: (Step | ChildPipelineStep)[];
  checks: Checks;
  deployments: { [key: string]: Deployment };
  maxMemoryPercentage: any;
  isFetchingMetrics: boolean;
  metricWarningLimit: number;
}

export const PipelineWarning = memo(
  ({
    account,
    repository,
    pipeline,
    step,
    steps,
    checks,
    deployments,
    maxMemoryPercentage,
    isFetchingMetrics,
    metricWarningLimit,
  }: Props) => {
    const { isHistoryRunSelected } = usePipelineRunHistoryContext();

    const isSelfHostedRunnerWarningEnabled = useStatsigGate(
      StatsigFeatureKeys.isSelfHostedRunnerWarningEnabled
    );

    const getMemoryUsageWarning = useCallback(
      (maxMemory: any, isFetching: boolean) => {
        const warningContainers = [] as any;
        let usageErrorKey = MetricUsageStatusType.Unknown;

        if (!isFetching && Object.keys(maxMemory).length > 0) {
          Object.keys(maxMemory).forEach(element => {
            if (
              maxMemory[element] >= METRICS_USAGE_ERROR_LIMIT &&
              step.hasError
            ) {
              usageErrorKey = MetricUsageStatusType.Error;
              warningContainers.push(element);
            } else if (maxMemory[element] >= metricWarningLimit) {
              warningContainers.push(element);
              if (usageErrorKey === MetricUsageStatusType.Unknown) {
                usageErrorKey = MetricUsageStatusType.Warning;
              }
            }
          });
        }

        return { usageErrorKey, warningContainers };
      },
      [step, metricWarningLimit]
    );

    const error = pipeline.error || step.error;
    const errorKey = error && error.key;

    const { usageErrorKey, warningContainers } = getMemoryUsageWarning(
      maxMemoryPercentage,
      isFetchingMetrics
    );

    const isPipelineDefined = !!pipeline.uuid;
    const hasError = !!errorKey;
    const isFailedSelfHosted =
      isSelfHostedRunnerWarningEnabled &&
      !step.isChildStep &&
      step.wasExecutedOnSelfHostedRunner &&
      hasError;

    const hasMetricsWarning =
      !step.isChildStep &&
      getFeatureFlagFromStep(step.featureFlags, STEP_METRICS_ENABLED) &&
      usageErrorKey !== MetricUsageStatusType.Unknown;

    const hasWarning =
      isPipelineDefined &&
      (hasError ||
        pipeline.isHalted ||
        hasMetricsWarning ||
        isFailedSelfHosted ||
        pipeline.isActionRequired);

    const renderWarningMessage = () => {
      if (hasMetricsWarning) {
        return (
          <MetricsUsageMessage
            status={usageErrorKey}
            stepSize={step.stepSize}
            warningContainers={warningContainers}
          />
        );
      }

      if (pipeline.isHalted) {
        return (
          <HaltedMessage
            repository={repository}
            pipeline={pipeline}
            steps={steps}
            checks={checks}
            deployments={deployments}
          />
        );
      }

      return (
        <WarningMessage
          account={account}
          repository={repository}
          pipeline={pipeline}
          error={error}
          isFailedSelfHosted={isFailedSelfHosted}
        />
      );
    };

    if (!hasWarning && !isHistoryRunSelected) {
      return null;
    }

    return (
      <PipelineWarningWrapper data-testid="pipeline-warning-wrapper">
        {isHistoryRunSelected && <PreviousRunMessage />}
        {hasWarning && renderWarningMessage()}
      </PipelineWarningWrapper>
    );
  }
);
